import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export interface TabItem {
  key: string; // = pathname (unique per open page)
  path: string; // the URL to navigate to for this tab, query string included
  title: string;
}

/**
 * A tab is identified by its **pathname only**, while `path` keeps the whole
 * URL. The same page with a different query string is that page in a new state
 * (a template setup step, a saved filter), not a second tab — but the pane
 * resolves itself from `path`, so the query has to be carried or the page can
 * never see it. See tabs/TabbedWorkspace.tsx.
 */
const tabKeyFor = (path: string) => path.split("?")[0];

interface TabsState {
  tabs: TabItem[];
  activeKey: string;
  /**
   * Open a tab for `path` (or focus it if already open) and make it active.
   * `path` is a full URL — pathname plus any query string.
   */
  openTab: (path: string, title: string) => void;
  setActive: (key: string) => void;
  removeTab: (key: string) => void;
  closeOthers: (key: string) => void;
  closeToRight: (key: string) => void;
  closeAll: () => void;
  updateTitle: (key: string, title: string) => void;
}

export const useTabsStore = create<TabsState>()(
  persist(
    (set) => ({
      tabs: [],
      activeKey: "",

      openTab: (path, title) =>
        set((state) => {
          const key = tabKeyFor(path);
          const existing = state.tabs.find((t) => t.key === key);
          if (existing) {
            // Keep the first meaningful title; don't overwrite a page-set title
            // with a generic default. The URL itself is followed, though: the
            // query string is state the page reads back, so leaving `path`
            // behind would freeze the page on whatever it opened with.
            if (existing.path === path) return { activeKey: key };
            return {
              activeKey: key,
              tabs: state.tabs.map((t) => (t.key === key ? { ...t, path } : t)),
            };
          }
          return {
            tabs: [...state.tabs, { key, path, title }],
            activeKey: key,
          };
        }),

      setActive: (key) => set({ activeKey: key }),

      removeTab: (key) =>
        set((state) => ({ tabs: state.tabs.filter((t) => t.key !== key) })),

      closeOthers: (key) =>
        set((state) => ({ tabs: state.tabs.filter((t) => t.key === key) })),

      closeToRight: (key) =>
        set((state) => {
          const idx = state.tabs.findIndex((t) => t.key === key);
          if (idx === -1) return state;
          return { tabs: state.tabs.slice(0, idx + 1) };
        }),

      closeAll: () => set({ tabs: [] }),

      updateTitle: (key, title) =>
        set((state) => ({
          tabs: state.tabs.map((t) =>
            t.key === key ? { ...t, title } : t
          ),
        })),
    }),
    {
      name: "app-tabs",
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);
